'use client'

/**
 * What the Feedback Desk prints for the values the API sends, and the sentences
 * built from the desk's window and lens (translation sweep, 31 Oct 2026).
 *
 * A status, category or priority is a VALUE: the desk compares it, filters on it
 * and sends it back, so it never changes. Only what is printed goes through a
 * key, and a value this build has no word for is printed as the API sent it.
 *
 * Nothing here is English the reader sees. The strings on the left of the maps
 * are what the API or lib/feedback/desk.ts send (they identify a value, as
 * 'Open' does), and every word on screen comes from messages/feedback-tickets.
 *
 * Why {window} is one variable and not five sentences per lens: the window
 * words are a time adverbial ("today", "last 7 days"). They agree with nothing
 * around them in any of the four languages and stand wherever the sentence puts
 * them, so one translated phrase fits every sentence that takes one.
 */
import type { ReactNode } from 'react'
import { relativeToNow } from '@/components/lms/live/live-range'
import { UserText } from '@/components/ui/user-text'
import { DATE_TIME, formatLearnerDate } from '@/components/user-portal/learner-i18n'
import type { AiAllowance } from '@/lib/ai/credit-state'
import { batchShortfallIn } from '@/lib/ai/quota-copy'
import { GENERIC_REFUSAL_KEY, logRefusal, refusalKey } from '@/lib/api/refusal-key'
import { addDays, nextWiderWindow, startOfDay, WINDOWS } from '@/lib/feedback/desk'
import { feedbackSubmitterLabel } from '@/lib/feedback/submitter'
import { useTranslation, type I18nContextValue } from '@/lib/i18n'
import { ENGLISH_MESSAGES } from '@/lib/i18n/catalogues'
import { translate } from '@/lib/i18n/translate'
import { parseApiDate } from '@/lib/time/parse-api-date'
import type { FeedbackLensKey, FeedbackWindowKey } from '@/lib/types/feedback'

export type Translate = I18nContextValue['t']

/**
 * The same words in English, for a caller with no reader's `t`: the super-admin
 * desk builds the table columns without one and is still an English screen.
 */
export const englishT: Translate = (key, params) => translate(ENGLISH_MESSAGES, ENGLISH_MESSAGES, key, params)

// ── Status, category, priority ──────────────────────────────────────────────

export const STATUS_KEYS: ReadonlyMap<string, string> = new Map([
  ['Open', 'admin.open'],
  ['In Progress', 'admin.inProgress'],
  ['Resolved', 'admin.resolved'],
  ['Closed', 'admin.closed'],
  ['Wont Fix', 'admin.wontFix'],
])

export const CATEGORY_KEYS: ReadonlyMap<string, string> = new Map([
  ['Bug Report', 'feedbackTickets.category.bug'],
  ['Feature Request', 'feedbackTickets.category.feature'],
  ['Change Request', 'feedbackTickets.category.change'],
  ['Removal Request', 'feedbackTickets.category.removal'],
  ['General', 'feedbackTickets.category.general'],
])

export const PRIORITY_KEYS: ReadonlyMap<string, string> = new Map([
  ['Low', 'admin.low'],
  ['Medium', 'admin.medium'],
  ['High', 'admin.high'],
])

const SUMMARY_TYPE_KEYS: ReadonlyMap<string, string> = new Map([
  ['Summary', 'feedbackTickets.fields.summary'],
  ['Clarification', 'feedbackTickets.summary.clarification'],
])

const SCREENSHOT_TYPE_KEYS: ReadonlyMap<string, string> = new Map([
  ['Auto', 'feedbackTickets.screenshots.typeAuto'],
  ['Uploaded', 'feedbackTickets.screenshots.typeUploaded'],
])

export const ENV_KEYS: ReadonlyMap<string, string> = new Map([
  ['staging', 'feedbackTickets.env.staging'],
  ['production', 'feedbackTickets.env.production'],
  ['none', 'feedbackTickets.env.none'],
])

/**
 * The fixed sentences the customer timeline endpoint writes in English
 * (apps/api/src/modules/feedback/utils/feedback-timeline.ts). A description can
 * also be a note someone typed, which is not in this map and stays as typed.
 */
export const API_TEXT_KEYS: ReadonlyMap<string, string> = new Map([
  ['Submitted', 'feedbackTickets.timeline.submitted'],
  ['In Progress', 'admin.inProgress'],
  ['Resolved', 'admin.resolved'],
  ['Closed', 'admin.closed'],
  ["Won't Fix", 'admin.wontFix'],
  ['Reopened', 'feedbackTickets.timeline.reopened'],
  ['Fix deployed', 'feedbackTickets.timeline.fixDeployed'],
  ['Verified', 'feedbackTickets.timeline.verified'],
  ['Your feedback was received.', 'feedbackTickets.timeline.received'],
  ['The team started working on this.', 'feedbackTickets.timeline.started'],
  ['A fix was completed for this ticket.', 'feedbackTickets.timeline.completed'],
  ['This ticket was closed.', 'feedbackTickets.timeline.closed'],
  ['The team decided not to action this ticket.', 'feedbackTickets.timeline.wontFix'],
  ['The ticket was reopened.', 'feedbackTickets.timeline.wasReopened'],
  ['The fix was deployed to production.', 'feedbackTickets.timeline.deployed'],
  ['The fix was confirmed to work.', 'feedbackTickets.timeline.confirmed'],
  ['You', 'feedbackTickets.timeline.you'],
  ['HumanFirewall team', 'feedbackTickets.timeline.team'],
  ['Tester', 'feedbackTickets.timeline.tester'],
])

/** The label as a string (a toast, a filter option); a value with no word is the value. */
function labelOf(keys: ReadonlyMap<string, string>, t: Translate, value: string): string {
  const key = keys.get(value)
  return key ? t(key) : value
}

export const statusText = (t: Translate, value: string) => labelOf(STATUS_KEYS, t, value)
export const categoryText = (t: Translate, value: string) => labelOf(CATEGORY_KEYS, t, value)
export const priorityText = (t: Translate, value: string) => labelOf(PRIORITY_KEYS, t, value)
export const envText = (t: Translate, value: string) => labelOf(ENV_KEYS, t, value)
export const screenshotTypeText = (t: Translate, value: string) => labelOf(SCREENSHOT_TYPE_KEYS, t, value)

/** Printed in the reader's language, or as the API sent it when this build has no word for it. */
function ApiValue({ keys, value }: { keys: ReadonlyMap<string, string>; value: string }): ReactNode {
  const { t } = useTranslation()
  const key = keys.get(value)
  return key ? t(key) : <UserText>{value}</UserText>
}

export const FeedbackStatusLabel = ({ value }: { value: string }) => <ApiValue keys={STATUS_KEYS} value={value} />
export const FeedbackCategoryLabel = ({ value }: { value: string }) => <ApiValue keys={CATEGORY_KEYS} value={value} />
export const FeedbackPriorityLabel = ({ value }: { value: string }) => <ApiValue keys={PRIORITY_KEYS} value={value} />
export const FeedbackSummaryType = ({ value }: { value: string }) => <ApiValue keys={SUMMARY_TYPE_KEYS} value={value} />
/** A timeline title, sentence or actor: a fixed API sentence reads translated, anything typed stays. */
export const FeedbackApiText = ({ value }: { value: string }) => <ApiValue keys={API_TEXT_KEYS} value={value} />

// ── The window and the lens ─────────────────────────────────────────────────

export const WINDOW_KEYS: Record<FeedbackWindowKey, { labelKey: string; phraseKey: string }> = {
  today: { labelKey: 'feedbackTickets.window.today', phraseKey: 'feedbackTickets.windowPhrase.today' },
  yesterday: { labelKey: 'feedbackTickets.window.yesterday', phraseKey: 'feedbackTickets.windowPhrase.yesterday' },
  '7d': { labelKey: 'feedbackTickets.window.last7', phraseKey: 'feedbackTickets.windowPhrase.last7' },
  '30d': { labelKey: 'feedbackTickets.window.last30', phraseKey: 'feedbackTickets.windowPhrase.last30' },
  all: { labelKey: 'feedbackTickets.window.all', phraseKey: 'feedbackTickets.windowPhrase.all' },
}

export const LENS_KEYS: Record<FeedbackLensKey, { labelKey: string; describeKey: string; showingKey: string; noneKey: string }> = {
  resolved: {
    labelKey: 'feedbackTickets.lens.resolved',
    describeKey: 'feedbackTickets.lensDescribe.resolved',
    showingKey: 'feedbackTickets.lensShowing.resolved',
    noneKey: 'feedbackTickets.lensNone.resolved',
  },
  raised: {
    labelKey: 'feedbackTickets.lens.raised',
    describeKey: 'feedbackTickets.lensDescribe.raised',
    showingKey: 'feedbackTickets.lensShowing.raised',
    noneKey: 'feedbackTickets.lensNone.raised',
  },
  verify: {
    labelKey: 'feedbackTickets.lens.verify',
    describeKey: 'feedbackTickets.lensDescribe.verify',
    showingKey: 'feedbackTickets.lensShowing.verify',
    noneKey: 'feedbackTickets.lensNone.verify',
  },
  reopened: {
    labelKey: 'feedbackTickets.lens.reopened',
    describeKey: 'feedbackTickets.lensDescribe.reopened',
    showingKey: 'feedbackTickets.lensShowing.reopened',
    noneKey: 'feedbackTickets.lensNone.reopened',
  },
  open: {
    labelKey: 'feedbackTickets.lens.open',
    describeKey: 'feedbackTickets.lensDescribe.open',
    showingKey: 'feedbackTickets.lensShowing.open',
    noneKey: 'feedbackTickets.lensNone.open',
  },
}

/** A chip: "Last 7 days". */
export const windowText = (t: Translate, win: FeedbackWindowKey) => t(WINDOW_KEYS[win].labelKey)
/** Inside a sentence: "last 7 days". */
export const windowPhrase = (t: Translate, win: FeedbackWindowKey) => t(WINDOW_KEYS[win].phraseKey)

/**
 * The window the super-admin desk means by the lower-case English label it
 * still hands the digest strip. Keeps its page working, and its tiles
 * translated, until it passes the key itself.
 */
export function windowFromLabel(label: string | undefined): FeedbackWindowKey {
  return WINDOWS.find((w) => w.label.toLowerCase() === label)?.key ?? 'all'
}

/** "tickets submitted today": a lens chip's tooltip. */
export const lensDescription = (t: Translate, lens: FeedbackLensKey, win: FeedbackWindowKey) =>
  t(LENS_KEYS[lens].describeKey, { window: windowPhrase(t, win) })

/** "Showing tickets submitted today." */
export const lensShowing = (t: Translate, lens: FeedbackLensKey, win: FeedbackWindowKey) =>
  t(LENS_KEYS[lens].showingKey, { window: windowPhrase(t, win) })

/** What emptied a narrowed desk, and the next thing to try. */
export function lensEmptyHint(t: Translate, lens: FeedbackLensKey, win: FeedbackWindowKey): string {
  const wider = nextWiderWindow(win)
  return t(LENS_KEYS[lens].noneKey, {
    window: windowPhrase(t, win),
    next: wider
      ? t('feedbackTickets.empty.tryWider', { wider: windowText(t, wider.key) })
      : t('feedbackTickets.empty.clearChips'),
  })
}

// ── A digest day ────────────────────────────────────────────────────────────

/** The labels lib/feedback/desk.ts daySummary() returns, each a sentence around its count. */
export const DAY_SUMMARY_KEYS: ReadonlyMap<string, string> = new Map([
  ['raised', 'feedbackTickets.daySummary.raised'],
  ['in progress', 'feedbackTickets.daySummary.inProgress'],
  ['already resolved', 'feedbackTickets.daySummary.alreadyResolved'],
  ['reopened', 'feedbackTickets.daySummary.reopened'],
  ['resolved', 'feedbackTickets.daySummary.resolved'],
  ['verified', 'feedbackTickets.daySummary.verified'],
  ['awaiting', 'feedbackTickets.daySummary.awaiting'],
  ['won’t fix', 'feedbackTickets.daySummary.wontFix'],
])

const LONG_DAY: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long' }

/** "Today · Wednesday 2 September", "Yesterday · …", "Friday 28 August", in the reader's language. */
export function dayHeading(t: Translate, locale: string, date: Date, now: Date = new Date()): string {
  // English keeps the desk's day-then-month order; a full tag is used as given.
  const tag = locale === 'en' ? 'en-GB' : locale
  const today = startOfDay(now)
  const long = formatLearnerDate(date, tag, LONG_DAY)
  if (date.getTime() === today.getTime()) return t('feedbackTickets.digest.dayToday', { date: long })
  if (date.getTime() === addDays(today, -1).getTime()) return t('feedbackTickets.digest.dayYesterday', { date: long })
  if (date.getFullYear() === now.getFullYear()) return long
  return formatLearnerDate(date, tag, { ...LONG_DAY, year: 'numeric' })
}

// ── People, times, refusals ─────────────────────────────────────────────────

/** Who filed a ticket: lib/feedback/submitter.ts decides, and only "Anonymous" is a word to translate. */
export function submitterText(
  t: Translate,
  ticket: { submittedByName?: string | null; submittedByEmail?: string | null },
): string {
  const nobody = !ticket.submittedByName?.trim() && !ticket.submittedByEmail?.trim()
  return nobody ? t('admin.anonymous') : feedbackSubmitterLabel(ticket)
}

/** "2 days ago"; a value that is not a date is shown as it came. */
export function timeAgo(iso: string, locale: string): string {
  const date = parseApiDate(iso)
  return date ? relativeToNow(date, Date.now(), locale) : iso
}

/** The full date and time, for a tooltip. */
export function dateAndTime(iso: string, locale: string): string {
  return formatLearnerDate(iso, locale, DATE_TIME) || iso
}

/**
 * Why a Quick Summary press was refused before anything was sent. The pre-check
 * decides; its own `reason` is English, so the sentence comes from the catalogue
 * under the same rule: the figures appear only when they may be shown.
 */
export function creditShortfall(t: Translate, requested: number, allowance: AiAllowance): string {
  return batchShortfallIn(t, {
    requested,
    creditsRemaining: allowance.creditsRemaining ?? 0,
    numberVisible: allowance.numberVisible,
  }).body
}

/**
 * The sentence for a refused action. What the API said goes to the console
 * (logRefusal); the reader gets a sentence from the catalogue: the one a status
 * picks, or `fallbackKey`, which says which action did not go through.
 */
export function refusalSentence(t: Translate, what: string, error: unknown, fallbackKey: string): string {
  logRefusal(what, error)
  const key = refusalKey(error)
  return t(key === GENERIC_REFUSAL_KEY ? fallbackKey : key)
}
